import { useEffect, useState, type FormEvent, type JSX } from 'react';
import {
  MAX_ADDRESSES_PER_NODE,
  MAX_ADDRESS_ENTRIES,
  entrySize,
  formatAddressEntry,
  groupAddresses,
  parseAddressEntry,
  type AddressEntry,
  type MonitorState,
  type NetworkNode,
  type NodeAddress,
  type ReplaceNodeAddresses,
} from '@netdeck/contracts';
import { useTranslation } from '../../shared/i18n';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { Button } from '../../shared/ui/Button';
import type { Status } from '../../shared/ui/StatusDot';
import { showsExtraAddresses } from './extraAddresses';
import { statesByAddress } from './status';
import { useReplaceNodeAddresses } from './api';

/**
 * Дополнительные адреса узла (ADR 035) — просмотр и правка.
 *
 * Раздел один и тот же на карте и в списке узлов: панель свойств там общая,
 * и два редактора одного списка разошлись бы в первом же правиле разбора.
 *
 * Параметры проверки здесь не спрашиваются: они наследуются от основного
 * монитора узла (ADR 036). Об этом сказано подписью — иначе первым вопросом
 * будет «а где тут интервал».
 *
 * У типов из `showsExtraAddresses` раздела нет вовсе: у одного устройства
 * один адрес.
 */
export function AddressesSection({
  node,
  addresses,
  live,
  canWrite,
}: {
  node: NetworkNode;
  /** Адреса этого узла из схемы площадки. */
  addresses: readonly NodeAddress[];
  live: Map<string, MonitorState>;
  canWrite: boolean;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);

  const replace = useReplaceNodeAddresses(node.siteId);

  // Правка закрывается при переходе к другому узлу — как и в свойствах узла:
  // иначе форма осталась бы открытой со списком предыдущего.
  useEffect(() => setEditing(false), [node.id]);

  // После вызова всех хуков: порядок вызовов обязан совпадать между
  // рендерами, а тип узла меняется прямо в соседней форме этой же панели.
  if (!showsExtraAddresses(node.kind)) {
    return null;
  }

  const ordered = [...addresses].sort((a, b) => a.position - b.position);
  const byAddress = statesByAddress(live);

  if (editing) {
    return (
      <AddressesForm
        entries={groupAddresses(ordered)}
        pending={replace.isPending}
        error={replace.error}
        onCancel={() => setEditing(false)}
        onSave={async (input) => {
          try {
            await replace.mutateAsync({ nodeId: node.id, input });
            setEditing(false);
          } catch {
            // Форма остаётся открытой с набранным текстом: закрыть её после
            // отказа значит потерять правку и не сказать об этом.
          }
        }}
      />
    );
  }

  return (
    <section className="flex flex-col gap-2 border-b border-line px-3.5 py-3">
      <div className="flex items-center gap-2">
        <span className="font-mono text-2xs tracking-[0.06em] text-faint uppercase">
          {t('nodes.addresses.title')}
        </span>
        {ordered.length > 0 ? (
          <span className="text-xs text-dim">
            {t('nodes.addressesCount', { count: ordered.length })}
          </span>
        ) : null}
      </div>

      {ordered.length === 0 ? (
        <p className="text-xs text-dim">{t('nodes.addresses.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {groupAddresses(ordered).map((entry) => (
            <li key={entry.from} className="flex items-baseline justify-between gap-2 text-sm">
              <span className="truncate font-mono text-muted" title={formatAddressEntry(entry)}>
                {formatAddressEntry(entry)}
              </span>
              <EntryState entry={entry} addresses={ordered} byAddress={byAddress} />
            </li>
          ))}
        </ul>
      )}

      {canWrite ? (
        <div className="flex items-center gap-2">
          <Button icon="pencil" size="sm" onClick={() => setEditing(true)}>
            {t('monitors.edit')}
          </Button>
          <span className="text-xs text-faint">{t('nodes.addresses.inherits')}</span>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Сколько адресов записи отвечает.
 *
 * У одиночного адреса называется само состояние, у диапазона — счёт: «шесть
 * из десяти» отвечает на вопрос, ради которого диапазон и заводят, а «частично
 * доступен» не отвечает ни на какой.
 */
function EntryState({
  entry,
  addresses,
  byAddress,
}: {
  entry: AddressEntry;
  addresses: readonly NodeAddress[];
  byAddress: Map<string, MonitorState>;
}): JSX.Element {
  const { t } = useTranslation();

  const size = entrySize(entry);
  const first = addresses.findIndex((address) => address.address === entry.from);
  const covered = first === -1 ? [] : addresses.slice(first, first + size);
  const statuses = covered.map((address) => byAddress.get(address.id)?.status ?? 'unknown');

  if (entry.to === null) {
    return (
      <span className="flex-none text-xs text-dim">{t(STATE_KEYS[statuses[0] ?? 'unknown'])}</span>
    );
  }

  const responding = statuses.filter((status) => status === 'up').length;

  return (
    <span className="flex-none font-mono text-xs text-dim">
      {t('nodes.addresses.responding', { responding, total: covered.length })}
    </span>
  );
}

/** Строка редактора. Хранится текстом: разбор идёт по мере набора. */
interface EntryRow {
  text: string;
}

/**
 * Редактор списка.
 *
 * Строка принимает и одиночный адрес, и диапазон — тем же разбором, что
 * и сервер (`parseAddressEntry`): форма, понимающая запись иначе, чем
 * платформа, обещала бы то, чего не будет.
 */
function AddressesForm({
  entries,
  pending,
  error,
  onCancel,
  onSave,
}: {
  entries: AddressEntry[];
  pending: boolean;
  error: Error | null;
  onCancel: () => void;
  onSave: (input: ReplaceNodeAddresses) => Promise<unknown> | void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const [rows, setRows] = useState<EntryRow[]>(
    entries.map((entry) => ({ text: formatAddressEntry(entry) })),
  );

  const filled = rows.filter((row) => row.text.trim() !== '');
  const parsed = filled.map((row) => parseAddressEntry(row.text));
  const total = parsed.reduce((sum, entry) => sum + (entry === null ? 0 : entrySize(entry)), 0);

  const broken = parsed.some((entry) => entry === null);
  const tooMany = total > MAX_ADDRESSES_PER_NODE;

  function submit(event: FormEvent): void {
    event.preventDefault();

    if (broken || tooMany) {
      return;
    }

    // Пустые строки отбрасываются, а не сохраняются: строка, которую начали
    // заполнять и бросили, не должна становиться записью — как и в свойствах.
    void onSave({ entries: parsed.filter((entry) => entry !== null) });
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-2 border-b border-line bg-row-alt px-3.5 py-3"
    >
      <span className="text-sm text-muted">{t('nodes.addresses.title')}</span>
      <p className="text-xs text-dim">{t('nodes.addresses.hint')}</p>

      {rows.map((row, index) => (
        <EntryField
          key={index}
          text={row.text}
          index={index}
          onChange={(text) => setRows(rows.map((item, i) => (i === index ? { text } : item)))}
          onRemove={() => setRows(rows.filter((_, i) => i !== index))}
        />
      ))}

      <div className="flex items-center gap-2">
        <Button
          icon="plus"
          size="sm"
          disabled={rows.length >= MAX_ADDRESS_ENTRIES}
          onClick={() => setRows([...rows, { text: '' }])}
        >
          {t('nodes.addresses.add')}
        </Button>
        <span className="text-xs text-faint">
          {t('nodes.addresses.total', { total, max: MAX_ADDRESSES_PER_NODE })}
        </span>
      </div>

      {tooMany ? (
        <span className="text-xs text-down-fg">
          {t('nodes.addresses.tooMany', { max: MAX_ADDRESSES_PER_NODE })}
        </span>
      ) : null}

      <div className="flex items-center gap-2">
        <Button
          icon="check"
          type="submit"
          variant="primary"
          size="sm"
          disabled={pending || broken || tooMany}
        >
          {pending ? t('sites.form.saving') : t('common.action.save')}
        </Button>
        <Button icon="close" size="sm" onClick={onCancel} disabled={pending}>
          {t('common.action.cancel')}
        </Button>
        {error ? <span className="text-sm text-down-fg">{errorText(error)}</span> : null}
      </div>
    </form>
  );
}

/**
 * Строка ввода записи.
 *
 * Цена записи называется до сохранения: «десять адресов» за одной строкой —
 * не то, что человек обязан считать в уме, а целая /24 за случайно набранной
 * границей тем более.
 */
function EntryField({
  text,
  index,
  onChange,
  onRemove,
}: {
  text: string;
  index: number;
  onChange: (text: string) => void;
  onRemove: () => void;
}): JSX.Element {
  const { t } = useTranslation();

  const entry = text.trim() === '' ? null : parseAddressEntry(text);
  const size = entry === null ? 0 : entrySize(entry);

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2">
        <input
          value={text}
          onChange={(event) => onChange(event.target.value)}
          placeholder={t('nodes.addresses.placeholder')}
          aria-label={t('nodes.addresses.rowAria', { index: index + 1 })}
          className="h-[30px] min-w-0 flex-1 rounded-sm border border-line-strong bg-raised px-2 font-mono text-sm text-fg placeholder:text-faint"
        />
        <Button
          icon="trash"
          size="sm"
          variant="danger"
          onClick={onRemove}
          title={t('nodes.addresses.deleteAria', { index: index + 1 })}
          aria-label={t('nodes.addresses.deleteAria', { index: index + 1 })}
        />
      </div>

      {text.trim() === '' ? null : entry === null ? (
        <span className="text-xs text-down-fg">{t('nodes.addresses.invalid')}</span>
      ) : size > 1 ? (
        <span className="text-xs text-faint">{t('nodes.addressesCount', { count: size })}</span>
      ) : null}
    </div>
  );
}

const STATE_KEYS: Record<Status, string> = {
  up: 'nodes.addresses.state.up',
  down: 'nodes.addresses.state.down',
  warn: 'nodes.addresses.state.down',
  unknown: 'nodes.addresses.state.unknown',
};
